import * as distance from '~/constants/distance';
import {
  POLYMAILER_DISTANCE_CM,
  POLYMAILER_DISTANCE_CM_VALUE,
  POLYMAILER_DISTANCE_IN,
  POLYMAILER_DISTANCE_IN_VALUE,
  POLYMAILER_DISTANCE_MM,
  POLYMAILER_DISTANCE_MM_VALUE,
} from '~/constants/polyMailerValues';
import * as weight from '~/constants/weight';
import CarrierParcelTemplate, * as carrierParcelTemplatefrom from '~/types/CarrierParcelTemplate';

interface BaseJSONParcelTemplate {
  distance_unit: string;
  height: Maybe<string>;
  is_default: boolean;
  length: Maybe<string>;
  name: string;
  object_created?: string;
  object_id: string;
  object_updated?: string;
  weight: string;
  weight_unit: string;
  width: string;
}

interface JSONParcelTemplate extends BaseJSONParcelTemplate {
  template: Maybe<carrierParcelTemplatefrom.JSONCarrierParcelTemplate>;
}

interface SavedJSONParcelTemplate extends BaseJSONParcelTemplate {
  template: string;
}

export type ParcelTemplate = {
  carrierTemplate: Maybe<CarrierParcelTemplate>;
  distanceUnit: distance.DistanceUnit;
  height: number;
  isDefault: boolean;
  length: number;
  name: string;
  objectCreated?: Date;
  objectId?: string;
  objectUpdated?: Date;
  weight: number;
  weightUnit: weight.WeightUnit;
  width: number;
};

export type WebUserParcelTemplate = {
  distance_unit: distance.DistanceUnit;
  height: string;
  is_default: boolean;
  length: string;
  name: string;
  object_id?: string;
  template?: carrierParcelTemplatefrom.WebCarrierParcelTemplate;
  weight: string;
  weight_unit: weight.WeightUnit;
  width: string;
};

const unitValue: ParcelTemplate = {
  carrierTemplate: undefined,
  distanceUnit: distance.DistanceUnit.IN,
  height: 0,
  isDefault: false,
  length: 0,
  name: '',
  objectId: undefined,
  weight: 0,
  weightUnit: weight.WeightUnit.LB,
  width: 0,
};

const fromJSON = (jsonParcelTemplate: JSONParcelTemplate): Maybe<ParcelTemplate> => {
  if (!jsonParcelTemplate) return null;

  const result: ParcelTemplate = {
    carrierTemplate: carrierParcelTemplatefrom.fromJSON(jsonParcelTemplate.template),
    distanceUnit: distance.distanceUnitMap[jsonParcelTemplate.distance_unit],
    height: Number(jsonParcelTemplate.height),
    isDefault: jsonParcelTemplate.is_default,
    length: Number(jsonParcelTemplate.length),
    name: jsonParcelTemplate.name,
    objectId: jsonParcelTemplate.object_id,
    weight: Number(jsonParcelTemplate.weight),
    weightUnit: weight.weightUnitMap[jsonParcelTemplate.weight_unit],
    width: Number(jsonParcelTemplate.width),
  };

  const { object_created, object_updated } = jsonParcelTemplate;
  if (object_created) result.objectCreated = new Date(object_created);

  if (object_updated) result.objectUpdated = new Date(object_updated);

  return result;
};

/*
 * This was added as conservative check, somewhere up the chain
 * when we are creating the ParcelTemplate object we use object spread (...)
 * operator and it somehow converts date in string.
 *
 * This cannot be simulated in the typescript environment, only in compiled environment
 *
 * We don't really care for the dates, so we just pass empty if its a object
 */
export const dateISOString = (date?: Date): string => {
  if (typeof date === 'object' && typeof date.toISOString === 'function') return date.toISOString();

  return '';
};

const toJSON = (parcelTemplate: ParcelTemplate): SavedJSONParcelTemplate => {
  const jsonParcelTemplate: SavedJSONParcelTemplate = {
    distance_unit: parcelTemplate.distanceUnit,
    height: parcelTemplate.height.toString(),
    is_default: parcelTemplate.isDefault,
    length: parcelTemplate.length.toString(),
    name: parcelTemplate.name,
    object_created: dateISOString(parcelTemplate.objectCreated),
    object_id: '',
    object_updated: dateISOString(parcelTemplate.objectUpdated),
    template: '',
    weight: parcelTemplate.weight.toString(),
    weight_unit: parcelTemplate.weightUnit,
    width: parcelTemplate.width.toString(),
  };

  if (parcelTemplate.objectId) jsonParcelTemplate.object_id = parcelTemplate.objectId;

  if (parcelTemplate.carrierTemplate)
    jsonParcelTemplate.template = parcelTemplate.carrierTemplate.token;

  return jsonParcelTemplate;
};

export const fromWebUserParcelTemplate = (template?: WebUserParcelTemplate): ParcelTemplate => {
  if (!template) return unitValue;

  const carrierTemplate = carrierParcelTemplatefrom.fromWebCarrierParcelTemplate(template.template);
  return {
    carrierTemplate,
    distanceUnit: template.distance_unit,
    height: Number(template.height),
    isDefault: template.is_default,
    length: Number(template.length),
    name: template.name,
    objectId: template.object_id,
    weight: Number(template.weight),
    weightUnit: template.weight_unit,
    width: Number(template.width),
  };
};

export type DimensionProvider = {
  distanceUnit: distance.DistanceUnit;
  height: number;
  length: number;
  width: number;
};

const getDimensionsString = (dimensionProvider: DimensionProvider): string => {
  if (
    dimensionProvider.length === 0 &&
    dimensionProvider.width === 0 &&
    dimensionProvider.height === 0
  ) {
    // softpack carrier template
    return '';
  }

  // get polymailer heights for selected unit
  const selectedPolymailerHeightInches =
    dimensionProvider?.distanceUnit === POLYMAILER_DISTANCE_IN &&
    dimensionProvider?.height === POLYMAILER_DISTANCE_IN_VALUE
      ? true
      : false;
  const selectedPolymailerHeightCm =
    dimensionProvider?.distanceUnit === POLYMAILER_DISTANCE_CM &&
    dimensionProvider?.height === POLYMAILER_DISTANCE_CM_VALUE
      ? true
      : false;
  const selectedPolymailerHeightMm =
    dimensionProvider?.distanceUnit === POLYMAILER_DISTANCE_MM &&
    dimensionProvider?.height === POLYMAILER_DISTANCE_MM_VALUE
      ? true
      : false;

  if (dimensionProvider.length && dimensionProvider.width && dimensionProvider.height === 0) {
    // user parcel template from softpack
    return `${dimensionProvider.length} x ${dimensionProvider.width} ${dimensionProvider.distanceUnit}`;
  }

  if (
    selectedPolymailerHeightInches ||
    selectedPolymailerHeightCm ||
    (selectedPolymailerHeightMm && dimensionProvider.length && dimensionProvider.width)
  )
    return `${dimensionProvider.length} x ${dimensionProvider.width} ${dimensionProvider.distanceUnit}`;

  return `${dimensionProvider.length} x ${dimensionProvider.width} x ${dimensionProvider.height} ${dimensionProvider.distanceUnit}`;
};

type WeightProvider = {
  weight: Maybe<number | string>;
  weightUnit: Maybe<string>;
};

const getWeightString = (weightProvider: WeightProvider): string =>
  weightProvider.weight ? `${weightProvider.weight} ${weightProvider.weightUnit}` : '';

const getName = (parcelTemplate: ParcelTemplate): string =>
  parcelTemplate.carrierTemplate ? parcelTemplate.carrierTemplate.name : parcelTemplate.name;

/**
 * Confirms whether parcel template is of type `ParcelTemplate`.
 *
 * For more info on the type guard pattern, see https://www.typescriptlang.org/docs/handbook/advanced-types.html#user-defined-type-guards
 *
 * @param parcelTemplate parcel template to type-check
 */
const isParcelTemplate = (
  parcelTemplate: CarrierParcelTemplate | ParcelTemplate,
): parcelTemplate is ParcelTemplate =>
  (<ParcelTemplate>parcelTemplate).carrierTemplate !== undefined;

export default ParcelTemplate;

export {
  CarrierParcelTemplate,
  JSONParcelTemplate,
  SavedJSONParcelTemplate,
  fromJSON,
  getDimensionsString,
  getName,
  getWeightString,
  isParcelTemplate,
  toJSON,
};
